import { useEffect, useRef, useState } from "react";
import type { WifiPickerConfig } from "../scenario/types";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";
import { audio } from "../lib/audio";

/**
 * Public Wi-Fi decision.
 * 1) Pick a network (no VERIFY/RISKY spoilers).
 * 2) Trap networks open a captive portal where Max's credentials are typed
 *    in visibly, then the learner submits or backs out.
 * Teaching happens AFTER the choice (teach beat), not on the buttons.
 */
export function WifiPicker({
  config,
  onChoose,
}: {
  config: WifiPickerConfig;
  onChoose: (net: WifiPickerConfig["networks"][number]) => void;
}) {
  const [portal, setPortal] = useState<WifiPickerConfig["networks"][number] | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phase, setPhase] = useState<"typing_in" | "ready" | "submitting" | "done">("typing_in");
  const spoke = useRef(false);
  const typeCancel = useRef(0);

  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.strangerLine) void audio.prefetchSpeech(config.strangerLine, { voice: "echo", lang: config.language ?? "en-US" });
    if (config.lenaRisky) void audio.prefetchSpeech(config.lenaRisky, { voice: "nova", lang: config.language ?? "en-US" });
    if (config.lenaSafe) void audio.prefetchSpeech(config.lenaSafe, { voice: "nova", lang: config.language ?? "en-US" });
    if (spoke.current) return;
    spoke.current = true;
    void audio.speak(config.body ?? "Pick a network for the client deck download.", {
      voice: config.voiceId ?? "onyx",
      lang: config.language ?? "en-US",
    });
    return () => audio.cancelSpeech();
  }, [config]);

  // When a trap portal opens, animate Max typing credentials into the fields.
  useEffect(() => {
    if (!portal) return;
    setEmail("");
    setPassword("");
    setPhase("typing_in");
    const emailTarget = "max.berger@company.com";
    const passTarget = "ClientDeal2026!";
    let i = 0;
    let j = 0;
    const token = ++typeCancel.current;
    const tick = () => {
      if (token !== typeCancel.current) return;
      if (i < emailTarget.length) {
        i += 1;
        setEmail(emailTarget.slice(0, i));
        window.setTimeout(tick, 45 + Math.random() * 35);
        return;
      }
      if (j < passTarget.length) {
        j += 1;
        setPassword(passTarget.slice(0, j));
        window.setTimeout(tick, 55 + Math.random() * 40);
        return;
      }
      setPhase("ready");
    };
    window.setTimeout(tick, 400);
    return () => {
      typeCancel.current += 1;
    };
  }, [portal]);

  if (portal) {
    const canSubmit = phase === "ready";
    return (
      <div className="rw-panel wifipicker portal">
        <div className="rw-kicker">Captive portal — {portal.ssid}</div>
        <h2>{config.captivePortalTitle ?? "Sign in to Wi-Fi"}</h2>
        <p className="rw-body">
          {config.captivePortalBody ??
            "This network wants company credentials before it connects. Watch Max type — then choose: submit the login, or disconnect and go back."}
        </p>
        <div className="wifi-fake-form">
          <label className="wifi-field">
            <span>Work email</span>
            <input
              value={email}
              readOnly
              placeholder="typing…"
              autoComplete="off"
              className={phase === "typing_in" ? "wifi-input--live" : undefined}
            />
          </label>
          <label className="wifi-field">
            <span>Password</span>
            <input
              value={password}
              readOnly
              type="text"
              placeholder="typing…"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              className={`cf-mask${phase === "typing_in" ? " wifi-input--live" : ""}`}
            />
          </label>
        </div>
        {phase === "typing_in" && (
          <p className="wifi-typing" aria-live="polite">
            Max is entering his company login…
          </p>
        )}
        {phase === "submitting" && (
          <p className="wifi-typing" aria-live="polite">
            Submitting credentials to the portal…
          </p>
        )}
        {config.strangerLine && phase === "ready" && (
          <p className="wifi-stranger">“{config.strangerLine}”</p>
        )}
        <div className="wifi-actions">
          <button
            type="button"
            className="btn btn-danger"
            disabled={!canSubmit}
            onClick={() => {
              setPhase("submitting");
              if (config.strangerLine)
                void audio.speak(config.strangerLine, {
                  voice: "echo",
                  lang: config.language ?? "en-US",
                });
              window.setTimeout(() => {
                if (config.lenaRisky)
                  void audio.speak(config.lenaRisky, {
                    voice: "nova",
                    lang: config.language ?? "en-US",
                  });
                setPhase("done");
                onChoose(portal);
              }, 1400);
            }}
          >
            Submit company login
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={phase === "submitting" || phase === "done"}
            onClick={() => {
              typeCancel.current += 1;
              setPortal(null);
              setEmail("");
              setPassword("");
              setPhase("typing_in");
            }}
          >
            Disconnect — pick another network
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rw-panel wifipicker">
      <div className="rw-kicker">Available networks</div>
      <h2>{config.headline ?? "Which Wi-Fi should Max use?"}</h2>
      <p className="rw-body">
        {config.body ??
          "Max needs the updated client deck before boarding. Tap one network to connect."}
      </p>
      <ChoiceList prompt="Tap a network">
        {config.networks.map((n) => (
          <ChoiceCard
            key={n.id}
            category={n.category}
            showRiskLabel={false}
            reply={`${n.ssid}${n.signal != null ? ` · ${n.signal}%` : ""}`}
            onClick={() => {
              if (n.trap) {
                setPortal(n);
                return;
              }
              if (n.trusted && config.lenaSafe)
                void audio.speak(config.lenaSafe, {
                  voice: "nova",
                  lang: config.language ?? "en-US",
                });
              else if (config.lenaRisky)
                void audio.speak(config.lenaRisky, {
                  voice: "nova",
                  lang: config.language ?? "en-US",
                });
              onChoose(n);
            }}
          />
        ))}
      </ChoiceList>
    </div>
  );
}
